import React from 'react';
import { LogoUpload, BannerUpload } from './Brands/components';
import BrandAssetsEmulator from './Brands/containers/BrandAssetsEmulator';
import BrandDetailsV1 from './Brands/containers/BrandDetails/BrandDetailsV1';
import BrandDetailsV2 from './Brands/containers/BrandDetails/BrandDetailsV2';
import BrandListingV1 from './Brands/containers/BrandListing/BrandListingV1';
import BrandListingV2 from './Brands/containers/BrandListing/BrandListingV2';
import BrandRegistration from './Brands/containers/BrandRegistration';
import AcceptedCnpCampaigns from './Campaigns/containers/AcceptedCnpCampaigns';
import CampaignDetail from './Campaigns/containers/CampaignDetailV2';
import CampaignRedirector from './Campaigns/containers/CampaignRedirector';
import CampaignRegisterSuccess from './Campaigns/containers/CampaignRegisterSuccess';
import CampaignRegistrationV2 from './Campaigns/containers/CampaignRegistrationV2';
import CnpCampaignsPage from './Campaigns/containers/CnpCampaignsPage';
import MyCampaigns from './Campaigns/containers/MyCampaigns';
import MyCampaignsHU from './Campaigns/containers/MyCampaignsHU';
import CampaignDetailHU from './Campaigns/containers/CampaignDetailHU';
import RcsCampaignsContainer from './Campaigns/containers/RcsCampaignsContainer';
import PartnerCampaignDetail from './Campaigns/containers/PartnerCampaignDetailV2';
import PendingCnpCampaigns from './Campaigns/containers/PendingCnpCampaigns';
import PendingCnpRcsCampaigns from './Campaigns/containers/PendingCnpRcsCampaigns';
import AcceptedCnpRcsCampaigns from './Campaigns/containers/AcceptedCnpRcsCampaigns';
import CspRegistrationWelcome from './CspRegistration/containers/CspRegistrationWelcome';
import CspRegistrationContainer from './CspRegistration/containers/CspRegistrationContainer';
import CspVerificationStatus from './CspRegistration/containers/CspVerificationStatus';
import DashboardNew from './Dashboard/containers/DashboardNew';
import EventsListV1 from './Events/containers/EventsList';
import EventsListDE from './Events/containers/EventsListDE';
import Integrations from './Integrations/containers/Integrations';
import KnowledgeBaseNew from './Knowledge Base/containers/KnowledgeBase';
import ProfileManagementV2 from './Profile/containers/ProfileManagementV2';
import ProfileManagementDE from './Profile/containers/ProfileManagementDE';
import Resellers from './Resellers/containers/Resellers';
import RcsCampaignDetail from './Campaigns/containers/RcsCampaignDetail';
import {
  ASSET_LINK_UPLOAD_ENABLED,
  DECLINED_CNP_CAMPAIGNS_ENABLED,
} from '@/constants';
import {
  BRAND_DETAILS_PATH,
  BRANDS_PATH,
  CNP_CAMPAIGN_DETAILS_PATH,
  CNP_CAMPAIGNS_HISTORY_PATH,
  CNP_CAMPAIGNS_PATH,
  MY_CAMPAIGN_DETAILS_PATH,
  MY_CAMPAIGNS_PATH,
  RCS_CAMPAIGNS_PATH,
  CNP_RCS_CAMPAIGNS_PATH,
  CNP_RCS_CAMPAIGNS_HISTORY_PATH,
  RCS_CAMPAIGN_DETAILS_PATH,
  CNP_RCS_CAMPAIGN_DETAILS_PATH,
  WILDCARD_CAMPAIGN_DETAILS_PATH,
  DECLINED_CNP_CAMPAIGNS_PATH,
  DASHBOARD_PATH,
} from '@/constants/paths';
import { Role, RouteConfig } from '@/types';

const AllRoles = Object.values(Role);

const LegacyCnpCampaignRoutes: RouteConfig[] = [
  {
    key: 'cnp-campaigns-history-list',
    path: CNP_CAMPAIGNS_HISTORY_PATH,
    title: 'History of Campaigns by Partners',
    component: AcceptedCnpCampaigns,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'cnp-campaigns-list',
    path: CNP_CAMPAIGNS_PATH,
    title: '10DLC CNP Campaigns',
    component: PendingCnpCampaigns,
    roles: AllRoles,
    countries: ['US'],
  },
];

const NewCnpCampaignRoutes: RouteConfig[] = [
  {
    key: 'cnp-campaigns',
    path: [
      CNP_CAMPAIGNS_HISTORY_PATH,
      CNP_CAMPAIGNS_PATH,
      DECLINED_CNP_CAMPAIGNS_PATH,
    ],
    title: '10DLC CNP Campaigns',
    component: CnpCampaignsPage,
    roles: AllRoles,
    countries: ['US'],
  },
];

const routes: RouteConfig[] = [
  {
    key: 'csp-account',
    path: '/user/csp',
    title: 'Welcome to The Campaign Registry',
    component: CspRegistrationWelcome,
    roles: AllRoles,
  },
  {
    key: 'csp-form',
    path: '/user/csp/create',
    title: 'CSP Verification',
    component: CspRegistrationContainer,
    roles: AllRoles,
  },
  {
    key: 'csp-status',
    path: '/user/csp/status',
    title: 'Approval Status',
    component: CspVerificationStatus,
    roles: AllRoles,
  },
  {
    key: 'dashboard',
    path: DASHBOARD_PATH,
    title: 'Dashboard',
    component: DashboardNew,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'profileUS',
    path: '/profile',
    title: 'CSP Profile',
    component: ProfileManagementV2,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'profileDE',
    path: '/profile',
    title: 'CSP Profile',
    component: ProfileManagementDE,
    roles: AllRoles,
    countries: ['DE'],
  },
  {
    key: 'brand-create',
    path: '/brand/create',
    title: 'Register a Brand',
    component: BrandRegistration,
    roles: [Role.User, Role.Manager],
    countries: ['US'],
  },
  {
    key: 'brandDetailsUS',
    path: BRAND_DETAILS_PATH,
    title: 'Brands',
    component: BrandDetailsV1,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'brandDetailsDE',
    path: BRAND_DETAILS_PATH,
    title: 'Brands',
    component: BrandDetailsV2,
    roles: AllRoles,
    countries: ['DE'],
  },
  ...(ASSET_LINK_UPLOAD_ENABLED
    ? []
    : [
        {
          key: 'brand-logo-upload',
          path: `${BRAND_DETAILS_PATH}/upload/logo`,
          title: 'Logo Upload',
          component: LogoUpload,
          roles: [Role.User, Role.Manager],
          countries: ['US'],
        },
        {
          key: 'brand-banner-upload',
          path: `${BRAND_DETAILS_PATH}/upload/banner`,
          title: 'Banner Upload',
          component: BannerUpload,
          roles: [Role.User, Role.Manager],
          countries: ['US'],
        },
      ]),
  {
    key: 'brand-assets',
    path: `${BRAND_DETAILS_PATH}/emulator`,
    title: 'Brands',
    component: BrandAssetsEmulator,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'brandsListUS',
    path: BRANDS_PATH,
    title: 'Brands',
    component: BrandListingV1,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'brandsListDE',
    path: BRANDS_PATH,
    title: 'Brands',
    component: BrandListingV2,
    roles: AllRoles,
    countries: ['DE'],
  },
  {
    key: 'campaign-create-success',
    path: '/campaign/create/successfully',
    title: 'Register a Campaign',
    component: CampaignRegisterSuccess,
    roles: [Role.User, Role.Manager],
    countries: ['US'],
  },
  {
    key: 'campaign-create-class',
    path: '/campaign/create',
    title: 'Register a Campaign',
    component: CampaignRegistrationV2,
    roles: [Role.User, Role.Manager],
    countries: ['US'],
  },
  {
    key: 'campaign-details',
    path: MY_CAMPAIGN_DETAILS_PATH,
    title: '10DLC campaign',
    component: CampaignDetail,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'campaigns-list',
    path: MY_CAMPAIGNS_PATH,
    title: 'MY 10DLC CAMPAIGNS',
    component: MyCampaigns,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'campaigns-list-HU',
    path: '/programs',
    title: 'My Programs',
    component: MyCampaignsHU,
    roles: AllRoles,
    countries: ['HU'],
  },
  {
    key: 'campaigns-detail-HU',
    path: '/programs/:id',
    title: 'Messaging program',
    component: CampaignDetailHU,
    roles: AllRoles,
    countries: ['HU'],
  },
  ...(DECLINED_CNP_CAMPAIGNS_ENABLED
    ? NewCnpCampaignRoutes
    : LegacyCnpCampaignRoutes),
  {
    key: 'cnp-campaign-details',
    path: CNP_CAMPAIGN_DETAILS_PATH,
    title: '10DLC Campaign',
    component: PartnerCampaignDetail,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'rcs-campaigns',
    path: RCS_CAMPAIGNS_PATH,
    title: 'My RCS Campaigns',
    component: RcsCampaignsContainer,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'rcs-cnp-campaigns-history',
    path: CNP_RCS_CAMPAIGNS_HISTORY_PATH,
    title: 'RCS CNP Campaigns',
    component: AcceptedCnpRcsCampaigns,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'rcs-cnp-campaigns',
    path: CNP_RCS_CAMPAIGNS_PATH,
    title: 'RCS CNP Campaigns',
    component: PendingCnpRcsCampaigns,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'rcs-campaign-detail',
    path: RCS_CAMPAIGN_DETAILS_PATH,
    title: 'RCS Campaign',
    roles: AllRoles,
    countries: ['US'],
    component: () => RcsCampaignDetail({ csp: true }),
  },
  {
    key: 'cnp-rcs-campaign-detail',
    path: CNP_RCS_CAMPAIGN_DETAILS_PATH,
    title: 'RCS CNP Campaign',
    roles: AllRoles,
    countries: ['US'],
    component: () => RcsCampaignDetail({ csp: false }),
  },
  {
    key: 'campaign-redirector',
    path: WILDCARD_CAMPAIGN_DETAILS_PATH,
    title: 'Campaign',
    roles: AllRoles,
    countries: ['US'],
    component: CampaignRedirector,
  },
  {
    key: 'eventsListUS',
    path: '/events',
    title: 'Events',
    component: EventsListV1,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'eventsListDE',
    path: '/events',
    title: 'Events',
    component: EventsListDE,
    roles: AllRoles,
    countries: ['DE'],
  },
  {
    key: 'knowledge-base-new',
    path: '/knowledge-base',
    title: 'Knowledge Base',
    component: KnowledgeBaseNew,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'edit-user',
    path: '/resellers',
    title: 'Resellers',
    component: Resellers,
    roles: AllRoles,
    countries: ['US'],
  },
  {
    key: 'integrations',
    path: '/integrations',
    title: 'Integrations',
    component: Integrations,
    roles: [Role.User, Role.Manager],
    countries: ['US'],
  },
];

export default routes;
